import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeLink, setActiveLink] = useState('/home');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setActiveLink(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isMobileMenuOpen]);

  const leftLinks = [
    { name: 'HOME', path: '/home' },
    { name: 'ABOUT US', path: '/about' },
    { name: 'OUR SERVICES', path: '/our-services' },
  ];

  const rightLinks = [
    { name: 'MEDIA & INSIGHTS', path: '/media-insights' },
    { name: 'PRACTICE AREAS', path: 'practice-areas' },
  ];

  const allLinks = [...leftLinks, ...rightLinks];

  const handleNavClick = (path: string) => {
    setIsMobileMenuOpen(false);

    // Handle Practice Areas anchor scroll
    if (path === 'practice-areas') {
      const isOnHome = location.pathname === '/home' || location.pathname === '/';
      if (isOnHome) {
        const section = document.getElementById('practice-areas');
        if (section) {
          section.scrollIntoView({ behavior: 'smooth' });
        }
      } else {
        navigate('/#practice-areas');
        // After navigation, scroll to section
        setTimeout(() => {
          const section = document.getElementById('practice-areas');
          if (section) {
            section.scrollIntoView({ behavior: 'smooth' });
          }
        }, 300);
      }
      return;
    }

    setActiveLink(path);
    window.REACT_APP_NAVIGATE(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const linkClass = (path: string) => {
    const isPracticeAreas = path === 'practice-areas';
    const isActive = isPracticeAreas
      ? false
      : activeLink === path;
    return `text-xs xl:text-sm font-semibold tracking-widest transition-colors duration-200 whitespace-nowrap cursor-pointer ${
      isActive ? 'text-[#6A1B9A]' : 'text-[#1a1a1a] hover:text-[#6A1B9A]'
    }`;
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-400 bg-white ${
          isScrolled ? 'shadow-lg py-2' : 'shadow-sm py-3'
        }`}
      >
        <div className="w-full px-4 sm:px-6 md:px-10">
          <div className="flex items-center justify-between gap-2">

            {/* Left nav links */}
            <div className="hidden lg:flex items-center gap-6 xl:gap-10 flex-1">
              {leftLinks.map((link) => (
                <button key={link.path} onClick={() => handleNavClick(link.path)} className={linkClass(link.path)}>
                  {link.name}
                </button>
              ))}
            </div>

            {/* Centered Logo */}
            <div
              className="cursor-pointer flex-shrink-0 flex items-center justify-center"
              onClick={() => handleNavClick('/home')}
            >
              <img
                src="https://static.readdy.ai/image/455d4b17bb0830d9dc0ba5f28e276371/eb9a6cafd9005b98ef52579f75a7ce88.png"
                alt="Mohuba & Associates"
                className="h-28 sm:h-32 md:h-36 w-auto object-contain"
              />
            </div>

            {/* Right nav links + CTA */}
            <div className="hidden lg:flex items-center gap-6 xl:gap-10 flex-1 justify-end">
              {rightLinks.map((link) => (
                <button key={link.path} onClick={() => handleNavClick(link.path)} className={linkClass(link.path)}>
                  {link.name}
                </button>
              ))}
              <button
                onClick={() => handleNavClick('/contact')}
                className="ml-4 px-5 py-2 bg-[#6A1B9A] text-white text-xs font-bold tracking-widest uppercase hover:bg-[#8E24AA] transition-all duration-300 whitespace-nowrap cursor-pointer shadow-md"
              >
                CONTACT US
              </button>
            </div>

            {/* Mobile Hamburger */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden z-50 w-10 h-10 flex flex-col items-center justify-center gap-[5px] cursor-pointer flex-shrink-0"
              aria-label="Toggle menu"
            >
              <span className={`block w-6 h-[2px] transition-all duration-300 origin-center bg-[#1a1a1a] ${isMobileMenuOpen ? 'rotate-45 translate-y-[7px]' : ''}`}></span>
              <span className={`block w-6 h-[2px] transition-all duration-300 bg-[#1a1a1a] ${isMobileMenuOpen ? 'opacity-0 scale-x-0' : ''}`}></span>
              <span className={`block w-6 h-[2px] transition-all duration-300 origin-center bg-[#1a1a1a] ${isMobileMenuOpen ? '-rotate-45 -translate-y-[7px]' : ''}`}></span>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-40 lg:hidden transition-opacity duration-300 ${
          isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setIsMobileMenuOpen(false)}></div>
        <div className={`absolute top-0 right-0 h-full w-[75vw] max-w-[300px] bg-[#0d0d0d] shadow-2xl transition-transform duration-300 ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
          <div className="flex flex-col h-full pt-36 px-6">
            <div className="flex flex-col gap-1">
              {allLinks.map((link) => (
                <button
                  key={link.path}
                  onClick={() => handleNavClick(link.path)}
                  className={`text-left px-4 py-3 text-sm font-semibold tracking-widest transition-all duration-200 whitespace-nowrap cursor-pointer border-b border-white/10 ${
                    activeLink === link.path ? 'text-[#6A1B9A]' : 'text-white hover:text-[#6A1B9A]'
                  }`}
                >
                  {link.name}
                </button>
              ))}
            </div>
            <button
              onClick={() => handleNavClick('/contact')}
              className="mt-6 w-full px-6 py-3 bg-[#6A1B9A] text-white font-bold text-sm tracking-widest uppercase hover:bg-[#8E24AA] transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              BOOK NOW
            </button>
            <div className="mt-auto pb-8 space-y-3 border-t border-white/10 pt-6">
              <div className="flex items-center gap-3">
                <i className="ri-phone-line text-[#BA68C8] text-lg"></i>
                <a href="tel:0795808857" className="text-sm text-white/80 hover:text-[#BA68C8] transition-colors">079 580 8857</a>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-mail-line text-[#BA68C8] text-lg mt-0.5"></i>
                <a href="mailto:info@mohubapartners.co.za" className="text-sm text-white/80 hover:text-[#BA68C8] transition-colors break-all">info@mohubapartners.co.za</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
